/**
 * useGoogleApiLoader
 *
 * Client-side hook to load the Google Maps JavaScript API (Places + Geocoding).
 * Purpose: ensure the Maps script is loaded exactly once in the browser,
 * prevent duplicate <script> insertion across components, and provide a
 * simple `{ isLoaded, loadError }` state for UI that depends on geocoding
 * (for example `SearchLocation`). This hook is SSR-safe (no effect runs
 * on the server) and will reuse an existing script tag when present to
 * avoid race conditions and redundant network requests.
 */
import { useEffect, useState } from "react";

export function useGoogleApiLoader() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if ((window as any).google && (window as any).google.maps) {
      setIsLoaded(true);
      return;
    }

    const key = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
    if (!key) {
      setLoadError(new Error("Missing NEXT_PUBLIC_GOOGLE_MAPS_API_KEY"));
      return;
    }

    // Avoid adding multiple script tags. If one exists, reuse it and attach handlers.
    const existing = Array.from(document.getElementsByTagName("script")).find(
      (s) => s.src?.includes("maps.googleapis.com/maps/api/js"),
    );

    if (existing) {
      // If already loaded, mark as loaded. Otherwise attach listeners.
      if ((window as any).google && (window as any).google.maps) {
        setIsLoaded(true);
        return;
      }

      const onLoad = () => setIsLoaded(true);
      const onError = () =>
        setLoadError(new Error("Failed to load Google Maps script"));
      existing.addEventListener("load", onLoad);
      existing.addEventListener("error", onError);

      return () => {
        existing.removeEventListener("load", onLoad);
        existing.removeEventListener("error", onError);
      };
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&libraries=places,geocoding`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsLoaded(true);
    script.onerror = () =>
      setLoadError(new Error("Failed to load Google Maps script"));
    document.head.appendChild(script);

    return () => {
      script.onload = null;
      script.onerror = null;
    };
  }, []);

  return { isLoaded, loadError };
}
